import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon } from 'sweetalert2';


@Injectable({
  providedIn: 'root',
})
export class ToastrService {
  constructor() { }

  //1. Standard alert
  showAlert(
    title: string,
    text: string,
    icon: SweetAlertIcon = 'info',
    timer: number = 2000
  ) {
    Swal.fire({
      title,
      text,
      icon,
      timer,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  }

  //2. Toast-style alert (top-right)
  showToast(
    message: string,
    icon: SweetAlertIcon = 'success',
    position: 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start' = 'top-end',
    timer: number = 2000
  ) {
    Swal.fire({
      toast: true,
      position,
      showConfirmButton: false,
      timer,
      timerProgressBar: true,
      icon,
      title: message,
      customClass: {
        popup: `custom-toast ${icon}-toast`, // Add icon-based class
      }
    });
  }

  // 3. Auto-title by component
  notifyComponentStatus(
    componentName: string,
    status: 'created' | 'updated' | 'deleted' | 'error',
    icon?: SweetAlertIcon
  ) {
    let title = '';
    let text = '';
    let statusIcon: SweetAlertIcon = 'info';

    switch (status) {
      case 'created':
        title = 'Created!';
        text = `${componentName} has been created successfully.`;
        statusIcon = 'success';
        break;
      case 'updated':
        title = 'Updated!';
        text = `${componentName} has been updated successfully.`;
        statusIcon = 'success';
        break;
      case 'deleted':
        title = 'Deleted!';
        text = `${componentName} has been removed.`;
        statusIcon = 'warning';
        break;
      case 'error':
        title = 'Error!';
        text = `Something went wrong with ${componentName}.`;
        statusIcon = 'error';
        break;
    }

    this.showAlert(title, text, icon || statusIcon);
  }

  // 4. Confirm dialog
  confirmDialog(title: string, text: string): Promise<boolean> {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'No, Cancel',
      buttonsStyling: false, // Disable default SweetAlert2 styles
      customClass: {
        popup: 'custom-swal-popup',
        confirmButton: 'custom-confirm-btn',
        cancelButton: 'custom-cancel-btn',
        title: 'text-2xl font-extrabold text-text-main mt-4',
        htmlContainer: 'text-text-muted font-medium mb-6',
        icon: 'my-6'
      },
      showClass: {
        popup: 'animate-in fade-in zoom-in duration-300'
      },
      hideClass: {
        popup: 'animate-out fade-out zoom-out duration-200'
      }
    }).then((result: any) => result.isConfirmed);
  }

  /**
   * Show a processing modal with a custom spinner.
   * @param message Title of the modal
   * @returns A promise that resolves when the modal is closed
   */
  showProcessing(message: string) {
    return Swal.fire({
      title: message,
      html: `
      <div class="custom-spinner"></div>
    `,
      allowOutsideClick: false,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl bg-white shadow-lg no-scrollbar custom-processing-popup',
        htmlContainer: 'mt-4',
      },
      didOpen: () => {
        // No need for Swal.showLoading(), we have our own spinner
      }
    });
  }


  /**
   * Prompt the user with a confirmation dialog, then show a processing modal with a custom spinner.
   * If the user confirms, this method returns true. Otherwise, it returns false.
   * @param entityName The name of the entity being deleted (e.g., 'announcement')
   * @param title The title of the confirmation dialog (defaults to 'Are you sure?')
   * @param processingMessage An optional custom message to show while processing (defaults to 'Deleting <entityName>...')
   * @returns A promise that resolves to true if the user confirms, or false if they cancel
   */
  async confirmWithProcessing(
    entityName: string,                        // e.g., 'announcement'
    title = 'Are you sure?',                   // dialog title
    processingMessage?: string                 // optional custom loader message
  ): Promise<boolean> {
    const finalText = `This action will permanently delete the ${entityName}.`;
    const loaderMessage = processingMessage || `Deleting ${entityName}...`;

    const confirmed = await this.confirmDialog(title, finalText);
    if (!confirmed) return false;

    this.showProcessing(loaderMessage);
    return true;
  }
}
